import { clamp } from '@theme/utilities';

/**
 * A non-interactive bar that mirrors how far a horizontal scroller has been scrolled.
 *
 * Peeking slides say "there is more to the right". They don't say "this strip moves",
 * which is the thing shoppers miss on touch — and on desktop there is no scrollbar at all,
 * because the scroll-snap carousels hide it. A thumb visibly shorter than its track says it.
 *
 * Reads scrollLeft/scrollWidth off the scroller directly rather than listening for a
 * slide-change event, so the thumb follows a finger mid-drag instead of jumping only once a
 * slide has settled. That also keeps it independent of any particular carousel component:
 * anything with `overflow-x` works.
 *
 * Placement contract: render the element as the scroller's next sibling, or point it at the
 * scroller with a `scroller` attribute holding a CSS selector resolved against the parent.
 */
export class CarouselProgress extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {number | null} */
  #rafId = null;

  connectedCallback() {
    const scroller = this.#resolveScroller();
    if (!(scroller instanceof HTMLElement)) return;

    this.#scroller = scroller;
    scroller.addEventListener('scroll', this.#handleScroll, { passive: true });

    // Watching the scroller alone only catches viewport changes. The thumb is sized from
    // scrollWidth, which also moves as slides settle after load (images, fonts, review
    // widgets), so watch the slides too.
    this.#resizeObserver.observe(scroller);
    for (const slide of scroller.children) this.#resizeObserver.observe(slide);

    this.#visibility.observe(this);
    this.#update();
  }

  disconnectedCallback() {
    this.#scroller?.removeEventListener('scroll', this.#handleScroll);
    this.#scroller = null;
    this.#resizeObserver.disconnect();
    this.#visibility.disconnect();

    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /** @returns {Element | null} */
  #resolveScroller() {
    const selector = this.getAttribute('scroller');
    if (selector) return this.parentElement?.querySelector(selector) ?? null;
    return this.previousElementSibling;
  }

  #handleScroll = () => {
    // The nudge has done its job the moment the carousel actually moves. Drop the observer
    // too, or a late first intersection would re-arm it behind the shopper's back.
    this.removeAttribute('hint');
    this.#visibility.disconnect();

    // Throttle with rAF to avoid layout thrashing, same as ScrollHint in scrolling.js.
    if (this.#rafId !== null) return;

    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      this.#update();
    });
  };

  #update = () => {
    const scroller = this.#scroller;
    if (!scroller) return;

    const { scrollWidth, clientWidth } = scroller;
    const overflow = scrollWidth - clientWidth;

    // A bar that can never move reads as a broken control, so drop it when everything fits.
    // This is also what makes the element safe at breakpoints where the carousel is off:
    // one group can be a carousel on mobile only, and the bar disappears on desktop.
    const scrollable = overflow >= 1 && this.#isHorizontallyScrollable(scroller);
    this.toggleAttribute('hidden', !scrollable);
    if (!scrollable) return;

    // scrollLeft runs negative in RTL.
    const progress = clamp(Math.abs(scroller.scrollLeft) / overflow, 0, 1);
    // Floored so a long carousel still leaves a thumb big enough to read as a thumb.
    const size = clamp(clientWidth / scrollWidth, 0.15, 1);

    this.style.setProperty('--carousel-progress-size', `${size}`);
    this.style.setProperty('--carousel-progress-start', `${progress * (1 - size)}`);
  };

  /**
   * A `flex-flow: row nowrap` container overflows its box whether or not it scrolls, so
   * scrollWidth > clientWidth alone would show a dead bar over a plain overflowing row.
   * Only `auto`/`scroll` can actually be dragged.
   *
   * @param {HTMLElement} scroller
   * @returns {boolean}
   */
  #isHorizontallyScrollable(scroller) {
    const overflowX = getComputedStyle(scroller).overflowX;
    return overflowX === 'auto' || overflowX === 'scroll';
  }

  #resizeObserver = new ResizeObserver(this.#update);

  /**
   * Nudges the thumb once the carousel is properly on screen. Without this the affordance
   * only pays off for shoppers who were already going to scroll. The keyframes themselves
   * are gated behind prefers-reduced-motion in the snippet's stylesheet.
   */
  #visibility = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        this.toggleAttribute('hint', true);
        this.#visibility.disconnect();
      }
    },
    { threshold: 0.75 }
  );
}

if (!customElements.get('carousel-progress')) {
  customElements.define('carousel-progress', CarouselProgress);
}
